// Settings card — Gmail send-only connection. When connected, all
// operator→client emails route through the operator's actual Gmail
// account so replies land in their inbox. Falls back to Resend when
// disconnected. Status, connect, and disconnect are all in here.
//
// Picks up the ?gmail=connected / ?gmail=failed query param the
// OAuth callback redirects back with and refreshes accordingly.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function apiFetch(path, init = {}) {
  const jwt = readPersistedJwtDirect();
  if (!jwt) throw new Error('Not signed in');
  const res = await fetch(path, {
    ...init,
    headers: { Authorization: `Bearer ${jwt}`, ...(init.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(body?.error || `HTTP ${res.status}`);
    err.status = res.status; err.body = body;
    throw err;
  }
  return body;
}

function fmtTimeAgo(iso) {
  if (!iso) return null;
  const t = Date.parse(iso); if (!Number.isFinite(t)) return null;
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1)     return 'just now';
  if (mins < 60)    return `${mins}m ago`;
  if (mins < 1440)  return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

export default function GmailConnectionPanel({ showToast }) {
  const [data, setData]   = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy]   = React.useState(false);
  const [err, setErr]     = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const j = await apiFetch('/api/google/gmail-status');
      setData(j);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // Surface the OAuth-callback redirect outcome via toast + reload.
  React.useEffect(() => {
    const url = new URL(window.location.href);
    const status = url.searchParams.get('gmail');
    if (!status) return;
    const email = url.searchParams.get('email');
    const error = url.searchParams.get('error');
    if (status === 'connected') {
      showToast?.(`Gmail connected${email ? ` as ${email}` : ''}.`, 'success');
    } else if (status === 'denied') {
      showToast?.('Gmail connection cancelled.', 'info');
    } else if (status === 'no_refresh_token') {
      showToast?.('Google didn\'t return a refresh token. Revoke access in your Google Account and retry.', 'error');
    } else if (status === 'failed') {
      showToast?.(`Gmail connection failed${error ? `: ${error}` : ''}.`, 'error');
    }
    // Clean URL so a manual refresh doesn't re-fire the toast.
    url.searchParams.delete('gmail');
    url.searchParams.delete('email');
    url.searchParams.delete('error');
    window.history.replaceState({}, '', url.toString());
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    setBusy(true);
    try {
      const j = await apiFetch('/api/google/oauth-start?json=1');
      if (j?.url) window.location.href = j.url;
      else throw new Error('No auth URL returned');
    } catch (e) {
      showToast?.(`Could not start Gmail connection: ${e.message}`, 'error');
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    const ok = await (window.hqConfirm ? window.hqConfirm({
      title: 'Disconnect Gmail?',
      body: 'Future emails will fall back to the default Resend sender (info@sixfigureagentos.com) until you reconnect.',
      confirmLabel: 'Disconnect', danger: true,
    }) : Promise.resolve(window.confirm('Disconnect Gmail?')));
    if (!ok) return;
    setBusy(true);
    try {
      await apiFetch('/api/google/gmail-disconnect', { method: 'POST' });
      showToast?.('Gmail disconnected.', 'success');
      await load();
    } catch (e) {
      showToast?.(`Disconnect failed: ${e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-bold mb-1">Email sender (Gmail)</h3>
        <div className="text-sm text-slate-500">Loading…</div>
      </div>
    );
  }

  const conn = data?.connection;
  const connected = !!data?.connected;
  const oauthConfigured = !!data?.oauth_configured;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
        <h3 className="font-bold">Email sender (Gmail)</h3>
        {connected && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-800 border-emerald-200">
            Connected
          </span>
        )}
        {!connected && conn?.status === 'revoked' && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-slate-100 text-slate-700 border-slate-200">
            Revoked
          </span>
        )}
        {!connected && conn?.status === 'error' && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-red-100 text-red-800 border-red-200">
            Needs reconnect
          </span>
        )}
      </div>

      <p className="text-sm text-slate-500 mb-4">
        Connect your Gmail so every email the CRM sends to a client comes from <em>your</em> address — replies land in your real inbox, not a shared support line.
        {!connected && ' When disconnected, emails fall back to the default sender.'}
      </p>

      {err && (
        <div className="mb-3 text-xs bg-red-50 border border-red-200 text-red-900 rounded-lg px-3 py-2">
          {err}
        </div>
      )}

      {!oauthConfigured && (
        <div className="mb-3 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2">
          Google OAuth isn't configured on the server (missing GOOGLE_OAUTH_CLIENT_ID / GOOGLE_OAUTH_CLIENT_SECRET). Connecting won't work until those are set.
        </div>
      )}

      {connected ? (
        <div className="space-y-3">
          <div className="border border-slate-200 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Sending as</div>
            <div className="text-sm font-semibold text-slate-900">{conn.gmail_address}</div>
            <div className="text-[11px] text-slate-500 mt-1.5">
              {conn.sends_total ? `${conn.sends_total} sent` : 'No sends yet'}
              {conn.last_sent_at && ` · last ${fmtTimeAgo(conn.last_sent_at)}`}
            </div>
            {conn.last_error && (
              <div className="text-[11px] text-red-700 mt-1.5">{conn.last_error}</div>
            )}
          </div>
          <button
            onClick={handleDisconnect}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 font-semibold"
          >
            {busy ? 'Disconnecting…' : 'Disconnect Gmail'}
          </button>
        </div>
      ) : (
        <div>
          {conn?.gmail_address && (
            <div className="mb-3 text-xs text-slate-600">
              Previously connected as <span className="font-semibold">{conn.gmail_address}</span>{conn.status === 'error' && conn.last_error ? ` — ${conn.last_error}` : ''}.
            </div>
          )}
          <button
            onClick={handleConnect}
            disabled={busy || !oauthConfigured}
            className="text-sm px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 font-semibold inline-flex items-center gap-2"
          >
            {busy ? 'Opening Google…' : (conn ? 'Reconnect Gmail' : 'Connect Gmail')}
          </button>
        </div>
      )}
    </div>
  );
}
